/**
 * DemoMessageLimitsSection
 *
 * Admin panel form section for configuring demo message limits per agent.
 * Renders input fields for:
 * - Límite de mensajes (total per link, default 100)
 * - Vigencia del link (days from creation, default 7)
 * - Mensajes de extensión (extra messages on extension, default 50)
 *
 * Landing page session limit is always 15 (hardcoded, not configurable).
 * Extension validity is always 3 days (hardcoded, not configurable).
 */

import { tokens } from '../../design-system/tokens';

interface DemoMessageLimitsSectionProps {
  messageLimit: number | null;
  validityDays: number | null;
  extensionAmount: number | null;
  onChange: (field: string, value: number | null) => void;
  errors: Record<string, string>;
}

interface FieldConfig {
  field: string;
  label: string;
  helpText: string;
  defaultValue: number;
  min: number;
  max: number;
}

const FIELDS: FieldConfig[] = [
  {
    field: 'messageLimit',
    label: 'Límite de mensajes',
    helpText: 'Total por link. Por defecto: 100 mensajes',
    defaultValue: 100,
    min: 1,
    max: 10000,
  },
  {
    field: 'validityDays',
    label: 'Vigencia del link (días)',
    helpText: 'Desde la fecha de creación. Por defecto: 7 días',
    defaultValue: 7,
    min: 1,
    max: 365,
  },
  {
    field: 'extensionAmount',
    label: 'Mensajes de extensión',
    helpText: 'Mensajes extra al extender. Por defecto: 50',
    defaultValue: 50,
    min: 1,
    max: 10000,
  },
];

export function DemoMessageLimitsSection({
  messageLimit,
  validityDays,
  extensionAmount,
  onChange,
  errors,
}: DemoMessageLimitsSectionProps) {
  const values: Record<string, number | null> = {
    messageLimit,
    validityDays,
    extensionAmount,
  };

  function handleChange(field: string, rawValue: string) {
    if (rawValue.trim() === '') {
      onChange(field, null);
      return;
    }
    const parsed = parseInt(rawValue, 10);
    if (!Number.isNaN(parsed)) {
      onChange(field, parsed);
    }
  }

  return (
    <div
      style={{
        padding: tokens.spacing[6],
        borderRadius: tokens.borderRadius.md,
        border: `1px solid ${tokens.colors.neutral[200]}`,
        backgroundColor: tokens.colors.neutral[50],
      }}
    >
      {/* Section title */}
      <h3
        style={{
          margin: 0,
          fontSize: tokens.typography.fontSize.lg,
          fontWeight: tokens.typography.fontWeight.semibold,
          color: tokens.colors.neutral[900],
          marginBottom: tokens.spacing[1],
        }}
      >
        Límites de Mensajes Demo
      </h3>
      <p
        style={{
          margin: 0,
          fontSize: tokens.typography.fontSize.sm,
          color: tokens.colors.neutral[500],
          marginBottom: tokens.spacing[5],
        }}
      >
        Dejá en blanco para usar los valores por defecto. El límite de sesión (landing) es siempre 15. La extensión siempre agrega 3 días.
      </p>

      {/* Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: tokens.spacing[5],
        }}
      >
        {FIELDS.map((cfg) => {
          const value = values[cfg.field];
          const error = errors[cfg.field];
          const hasError = Boolean(error);

          return (
            <div key={cfg.field}>
              <label
                htmlFor={`demo-limit-${cfg.field}`}
                style={{
                  display: 'block',
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.medium,
                  color: tokens.colors.neutral[700],
                  marginBottom: tokens.spacing[1],
                }}
              >
                {cfg.label}
              </label>
              <input
                id={`demo-limit-${cfg.field}`}
                type="number"
                min={cfg.min}
                max={cfg.max}
                step={1}
                value={value === null ? '' : value}
                onChange={(e) => handleChange(cfg.field, e.target.value)}
                placeholder={String(cfg.defaultValue)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: tokens.typography.fontSize.sm,
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${hasError ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                  backgroundColor: '#ffffff',
                  color: tokens.colors.neutral[900],
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: `border-color ${tokens.transitions.fast}`,
                }}
                aria-invalid={hasError}
                aria-describedby={
                  hasError
                    ? `demo-limit-${cfg.field}-error`
                    : `demo-limit-${cfg.field}-help`
                }
              />
              {/* Help text */}
              {!hasError && (
                <p
                  id={`demo-limit-${cfg.field}-help`}
                  style={{
                    margin: `${tokens.spacing[1]} 0 0`,
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.neutral[400],
                  }}
                >
                  {cfg.helpText}
                </p>
              )}
              {/* Error message */}
              {hasError && (
                <p
                  id={`demo-limit-${cfg.field}-error`}
                  role="alert"
                  style={{
                    margin: `${tokens.spacing[1]} 0 0`,
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.error.main,
                  }}
                >
                  {error}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
